import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Check, Zap, Crown } from 'lucide-react';

const ESSENTIALS_FEATURES = [
  "Desarrollo de aplicaciones móviles",
  "Alojamiento de proyectos privados",
  "Integración con Github",
  "Fork de tareas",
  "Capacidad de comprar créditos adicionales"
];

const PLUS_FEATURES = [
  "Todo lo incluido en Essentials",
  "Agente CloudT1",
  "Ventana de contexto 1M",
  "Pensamiento ultra",
  "Editar prompt del sistema",
  "Capacidad de crear agentes personalizados"
];

export const PricingSection = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubscribe = (planId) => {
    if (!user) {
      navigate('/register');
      return;
    }
    navigate(`/pricing?plan=${planId}`);
  };

  return (
    <section id="pricing" className="py-20 lg:py-32 px-4 bg-[#050505]">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="section-title text-center">{t('pricing.title')}</h2>
          <p className="section-subtitle text-center mx-auto">{t('pricing.subtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Essentials */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="pricing-card"
            data-testid="pricing-card-essentials"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Zap className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Essentials</h3>
            </div>

            <p className="text-gray-400 text-sm mb-6">Perfecto para constructores principiantes</p>

            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-4xl font-bold text-white">$15</span>
              <span className="text-gray-500">/ {t('pricing.monthly')}</span>
            </div>

            <p className="text-sm text-gray-400 mb-6">75 {t('pricing.credits')} / {t('pricing.monthly')}</p>

            {user?.plan === 'essentials' ? (
              <div className="w-full py-3 px-4 rounded-lg bg-white/5 border border-white/10 text-center text-gray-400 text-sm font-medium mb-6">
                {t('pricing.currentPlan')}
              </div>
            ) : (
              <button
                onClick={() => handleSubscribe('essentials')}
                className="w-full py-3 px-4 rounded-lg font-semibold text-sm btn-secondary mb-6"
                data-testid="subscribe-essentials-btn"
              >
                {t('pricing.subscribe')}
              </button>
            )}

            <div className="space-y-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Incluye:</p>
              {ESSENTIALS_FEATURES.map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Plus */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="pricing-card featured"
            data-testid="pricing-card-plus"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Crown className="w-5 h-5 text-orange-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Plus</h3>
            </div>

            <p className="text-gray-400 text-sm mb-6">Creado para creadores y marcas serias</p>

            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-4xl font-bold text-white">$100</span>
              <span className="text-gray-500">/ {t('pricing.monthly')}</span>
            </div>

            <p className="text-sm text-gray-400 mb-6">375 {t('pricing.credits')} / {t('pricing.monthly')}</p>

            {user?.plan === 'plus' ? (
              <div className="w-full py-3 px-4 rounded-lg bg-white/5 border border-white/10 text-center text-gray-400 text-sm font-medium mb-6">
                {t('pricing.currentPlan')}
              </div>
            ) : (
              <button
                onClick={() => handleSubscribe('plus')}
                className="w-full py-3 px-4 rounded-lg font-semibold text-sm btn-primary mb-6"
                data-testid="subscribe-plus-btn"
              >
                {t('pricing.subscribe')}
              </button>
            )}

            <div className="space-y-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Todo lo incluido en Essentials, más:</p>
              {PLUS_FEATURES.map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-orange-400" />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
